import type {
  Category,
  Choice,
  Config,
  Consent,
  ConsentChangeListener,
  Decision,
  PendingEvent,
  ReadyListener,
} from './types';
import {
  ALL_CATEGORIES,
  buildDecision,
  clearDecision,
  consentString,
  emptyConsent,
  fullConsent,
  writeDecision,
} from './storage';
import { pushDataLayerEvent, pushGcmDefault, pushGcmUpdate } from './gcm';
import { unblock } from './unblock';
import { renderBanner, removeBanner } from './banner';
import {
  clearPendingQueue,
  loadPendingQueue,
  postConsent,
  savePendingQueue,
} from './network';

export const SNIPPET_VERSION = '0.1.0';
export const UI_VERSION = 1;

type EventName = 'cookiesie:consent' | 'cookiesie:ready';

interface ApiState {
  projectId: string;
  config: Config;
  decision: Decision | null;
  ready: boolean;
  apiBase: string;
}

export interface CookiesieApi {
  init(opts?: { projectId?: string; configUrl?: string; locale?: 'en' | 'ga' }): void;
  getConsent(): Consent | null;
  hasDecision(): boolean;
  getCategory(c: Category): boolean;
  setConsent(c: Partial<Consent>): void;
  acceptAll(): void;
  rejectAll(): void;
  reopenPreferences(): void;
  withdrawConsent(): void;
  onConsentChange(cb: ConsentChangeListener): () => void;
  onReady(cb: ReadyListener): () => void;
  version: string;
  projectId: string;
}

export function createApi(state: ApiState): CookiesieApi {
  const consentListeners = new Set<ConsentChangeListener>();
  const readyListeners = new Set<ReadyListener>();

  function emit(name: EventName, detail?: Consent): void {
    const evt = detail
      ? new CustomEvent(name, { detail })
      : new Event(name);
    document.dispatchEvent(evt);
  }

  function fireConsent(c: Consent): void {
    consentListeners.forEach((cb) => {
      try {
        cb(c);
      } catch {}
    });
    emit('cookiesie:consent', c);
  }

  function fireReady(): void {
    state.ready = true;
    readyListeners.forEach((cb) => {
      try {
        cb();
      } catch {}
    });
    emit('cookiesie:ready');
  }

  function applyConsent(c: Consent, choice: Choice): void {
    const decision = buildDecision(c, state.config.policyRevision, choice);
    state.decision = decision;
    writeDecision(decision);
    pushGcmUpdate(c);
    unblock(c);
    pushDataLayerEvent(c);
    sendConsent(state, decision);
    fireConsent(c);
    removeBanner();
    bindFooterTriggers();
  }

  function reopen(): void {
    renderBanner(
      state.config,
      {
        onAcceptAll: () => applyConsent(fullConsent(), 'accept_all'),
        onRejectAll: () => applyConsent(emptyConsent(), 'reject_all'),
        onSavePartial: (c) => applyConsent(c, 'custom'),
      },
      state.decision?.c ?? null,
      true,
    );
  }

  function bindFooterTriggers(): void {
    document
      .querySelectorAll<HTMLAnchorElement>('a[href="#cookies-prefs"]')
      .forEach((a) => {
        if (a.hasAttribute('data-cookiesie-bound')) return;
        a.setAttribute('data-cookiesie-bound', '1');
        a.addEventListener('click', (e) => {
          e.preventDefault();
          reopen();
        });
      });
  }

  const api: CookiesieApi = {
    init() {
      // No-op: bootstrap already initialised; provided so test harnesses can
      // override projectId/configUrl/locale before they ship in Phase 2.
    },
    getConsent: () => (state.decision ? { ...state.decision.c } : null),
    hasDecision: () => !!state.decision,
    getCategory: (c) => !!state.decision?.c[c],
    setConsent: (partial) => {
      const next: Consent = { ...(state.decision?.c ?? emptyConsent()) };
      for (const k of ALL_CATEGORIES) {
        if (k in partial) next[k] = !!partial[k];
      }
      next.strictly_necessary = true;
      applyConsent(next, 'custom');
    },
    acceptAll: () => applyConsent(fullConsent(), 'accept_all'),
    rejectAll: () => applyConsent(emptyConsent(), 'reject_all'),
    reopenPreferences: () => reopen(),
    withdrawConsent: () => {
      clearDecision();
      state.decision = null;
      pushGcmUpdate(emptyConsent());
      reopen();
    },
    onConsentChange: (cb) => {
      consentListeners.add(cb);
      return () => consentListeners.delete(cb);
    },
    onReady: (cb) => {
      if (state.ready) {
        try {
          cb();
        } catch {}
      } else {
        readyListeners.add(cb);
      }
      return () => readyListeners.delete(cb);
    },
    version: SNIPPET_VERSION,
    projectId: state.projectId,
  };

  // Internal: surface for bootstrap to fire ready + bind footer triggers
  // without exposing them on the public API surface.
  (api as any)._fireReady = fireReady;
  (api as any)._bindFooter = bindFooterTriggers;
  (api as any)._renderBanner = (startInPrefs = false) =>
    renderBanner(
      state.config,
      {
        onAcceptAll: () => applyConsent(fullConsent(), 'accept_all'),
        onRejectAll: () => applyConsent(emptyConsent(), 'reject_all'),
        onSavePartial: (c) => applyConsent(c, 'custom'),
      },
      state.decision?.c ?? null,
      startInPrefs,
    );

  return api;
}

function buildPendingEvent(state: ApiState, decision: Decision): PendingEvent {
  return {
    projectId: state.projectId,
    choice: decision.choice,
    categories: decision.c,
    consentString: consentString(decision.c),
    uiVersion: UI_VERSION,
    snippetVersion: SNIPPET_VERSION,
    language: state.config.language,
    policyRevision: state.config.policyRevision,
    bannerTextHash: state.config.bannerTextHash ?? '',
    pageUrl: location.origin + location.pathname,
    ts: Date.now(),
  };
}

// Primary path: POST live with keepalive. Falls back to a localStorage
// retry queue (capped at 5 events per device, mvp_b_plan.md §5.3) which
// drainPending() flushes on next page load.
function sendConsent(state: ApiState, decision: Decision): void {
  // Skip the network entirely if we don't have a valid hash to attest with —
  // server would reject with 422 anyway, no point spending the round-trip.
  if (!state.projectId || !state.config.bannerTextHash) return;
  const evt = buildPendingEvent(state, decision);
  postConsent(`${state.apiBase}/consent`, evt).then((ok) => {
    if (ok) return;
    const queue = loadPendingQueue();
    queue.push(evt);
    savePendingQueue(queue);
  });
}

export async function drainPending(state: ApiState): Promise<void> {
  if (!state.projectId || !state.config.bannerTextHash) return;
  const queue = loadPendingQueue();
  if (queue.length === 0) return;
  const stillPending: PendingEvent[] = [];
  for (const evt of queue) {
    const ok = await postConsent(`${state.apiBase}/consent`, evt);
    if (!ok) stillPending.push(evt);
  }
  if (stillPending.length === 0) clearPendingQueue();
  else savePendingQueue(stillPending);
}

export { pushGcmDefault };
